import {
  Badge,
  Box,
  CircularProgress,
  Grid,
  GridItem,
  HStack,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import useAPI from '../../hooks/api';

const StreamTimeline = ({ streamRecord }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoverData, setHoverData] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState(null);
  
  const api = useAPI();
  const toast = useToast();

  const loadTimeline = useCallback(async () => {
    if (!streamRecord?.id) return;
    
    setLoading(true);
    try {
      const response = await api.getStreamRecordTimeline(streamRecord.id);
      setTimeline(response.data);
    } catch (error) {
      console.error('Error loading timeline:', error);
      toast({
        title: '載入失敗',
        description: '無法載入時間軸資料',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [api, streamRecord?.id, toast]);

  const loadIntervalRevenue = useCallback(async (startTime, endTime) => {
    if (!streamRecord?.id || loadingRevenue) return;
    
    setLoadingRevenue(true);
    try {
      const response = await api.getStreamRecordIntervalRevenue(
        streamRecord.id,
        startTime,
        endTime
      );
      setHoverData(response.data);
    } catch (error) {
      console.error('Error loading interval revenue:', error);
      // Don't show toast for hover errors as they're frequent
    } finally {
      setLoadingRevenue(false);
    }
  }, [api, streamRecord?.id, loadingRevenue]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const handleSegmentHover = useCallback((segment, index) => {
    setHoveredSegmentIndex(index);
    loadIntervalRevenue(segment.startTime, segment.endTime);
  }, [loadIntervalRevenue]);

  const handleSegmentLeave = useCallback(() => {
    setHoveredSegmentIndex(null);
    setHoverData(null);
  }, []);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const formatTime = (timestamp) => {
    return dayjs(timestamp * 1000).format('HH:mm:ss');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100px">
        <CircularProgress isIndeterminate size="sm" />
      </Box>
    );
  }

  if (!timeline) {
    return (
      <Box textAlign="center" p={4}>
        <Text color="gray.400">無法載入時間軸資料</Text>
      </Box>
    );
  }

  const totalDuration = timeline.totalDuration;

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Text fontWeight="bold" mb={2}>直播模式時間軸</Text>
        <Text fontSize="sm" color="gray.400" mb={4}>
          總時長：{formatDuration(totalDuration)} | 
          開始時間：{formatTime(timeline.startTime)} | 
          結束時間：{formatTime(timeline.endTime)}
        </Text>
      </Box>

      {/* Timeline visualization */}
      <Box position="relative" h="60px" bg="gray.800" borderRadius="md" p={2}>
        <Box position="relative" h="full">
          {timeline.modes.map((segment, index) => {
            const widthPercentage = (segment.duration / totalDuration) * 100;
            const leftPercentage = (segment.position / totalDuration) * 100;
            
            return (
              <Tooltip
                key={index}
                label={
                  <Box>
                    <Text fontSize="sm" fontWeight="bold">
                      {segment.mode === 'private' ? '私密模式' : '公開模式'}
                    </Text>
                    <Text fontSize="xs">
                      {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                    </Text>
                    <Text fontSize="xs">
                      時長: {formatDuration(segment.duration)}
                    </Text>
                    {loadingRevenue && hoveredSegmentIndex === index && (
                      <Text fontSize="xs" color="blue.300">載入收益資料...</Text>
                    )}
                    {hoverData && hoveredSegmentIndex === index && (
                      <Box mt={1} pt={1} borderTop="1px solid" borderColor="gray.500">
                        <Text fontSize="xs" color="green.300">
                          總收益: {hoverData.revenue.summary.totalDiamonds} 鑽石
                        </Text>
                        <Text fontSize="xs" color="purple.300">
                          禮物: {hoverData.revenue.summary.giftDiamonds} 鑽石
                        </Text>
                        {hoverData.revenue.privateRevenue && (
                          <Text fontSize="xs" color="orange.300">
                            私密: {hoverData.revenue.summary.privateDiamonds} 鑽石
                          </Text>
                        )}
                      </Box>
                    )}
                  </Box>
                }
                isOpen={hoveredSegmentIndex === index}
                placement="top"
              >
                <Box
                  position="absolute"
                  left={`${leftPercentage}%`}
                  width={`${widthPercentage}%`}
                  height="full"
                  bg={segment.mode === 'private' ? 'orange.500' : 'blue.500'}
                  borderRadius="sm"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    bg: segment.mode === 'private' ? 'orange.400' : 'blue.400',
                    transform: 'scaleY(1.1)',
                  }}
                  onMouseEnter={() => handleSegmentHover(segment, index)}
                  onMouseLeave={handleSegmentLeave}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Legend */}
      <HStack spacing={4} justify="center">
        <HStack spacing={2}>
          <Box w={4} h={4} bg="blue.500" borderRadius="sm" />
          <Text fontSize="sm">公開模式</Text>
        </HStack>
        <HStack spacing={2}>
          <Box w={4} h={4} bg="orange.500" borderRadius="sm" />
          <Text fontSize="sm">私密模式</Text>
        </HStack>
      </HStack>

      {/* Segments breakdown */}
      <Box>
        <Text fontWeight="bold" mb={2}>模式切換詳情</Text>
        <VStack spacing={2} align="stretch">
          {timeline.modes.map((segment, index) => (
            <Box
              key={index}
              bg="gray.700"
              p={3}
              borderRadius="md"
              borderLeft="4px solid"
              borderLeftColor={segment.mode === 'private' ? 'orange.500' : 'blue.500'}
            >
              <Grid templateColumns="repeat(4, 1fr)" gap={4} fontSize="sm">
                <GridItem>
                  <Text color="gray.300">模式</Text>
                  <Badge colorScheme={segment.mode === 'private' ? 'orange' : 'blue'}>
                    {segment.mode === 'private' ? '私密' : '公開'}
                  </Badge>
                </GridItem>
                <GridItem>
                  <Text color="gray.300">開始時間</Text>
                  <Text>{formatTime(segment.startTime)}</Text>
                </GridItem>
                <GridItem>
                  <Text color="gray.300">結束時間</Text>
                  <Text>{formatTime(segment.endTime)}</Text>
                </GridItem>
                <GridItem>
                  <Text color="gray.300">時長</Text>
                  <Text>{formatDuration(segment.duration)}</Text>
                </GridItem>
              </Grid>
            </Box>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

export default StreamTimeline;